import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError, AxiosHeaders } from 'axios';
import { useUserStore } from '../stores/userStore';
import { showError } from '../utils/notifications';
import { AUTH } from './apiPaths';
import { signInRedirectTarget } from '../utils/sessionRecovery';

// Create axios instance with base URL
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.GO_SERVER_API_URL || '/api',
  timeout: 30000, // Increased to 30 seconds for email operations
  headers: {
    'Content-Type': 'application/json'
  },
  // Enable sending cookies with requests when using cookie auth
  withCredentials: true
});

// Add a response interceptor to log raw responses
api.interceptors.response.use(
  (response) => {
   /* if (Array.isArray(response.data)) {
      console.log('Response is an array, first item keys:', Object.keys(response.data[0] || {}));
      if (response.data.length > 0) {
        console.log('First item in response:', response.data[0]);
      }
    }*/
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip token check for public routes (login, register, etc.)
    const publicRoutes = [
      AUTH.LOGIN,
      AUTH.REGISTER,
      AUTH.REFRESH,
      AUTH.VERIFY
    ];
    
    const isPublicRoute = publicRoutes.some(route => config.url?.endsWith(route));
    if (isPublicRoute) {
      console.log('Public route detected, skipping token check:', config.url);
      return config;
    }
    
    // Source of truth is the userStore state which syncs with cookie/localStorage
    const { token } = useUserStore.getState();
        
   
    if (!token) {
      console.warn('No authentication token found for protected route:', config.url);
      // Let the request proceed without a token and let the server handle the authentication
      // This prevents premature logout and allows proper error handling
      return config;
    }
    
    // Ensure headers object exists with proper type
    const headers = new AxiosHeaders(config.headers);
    
    // Add Authorization header with Bearer token format
    const authHeaderValue = `Bearer ${token}`;
    headers.set('Authorization', authHeaderValue);
    
    
    // Ensure content type is set
    if (!headers.get('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    
    // Assign the headers back to the config
    config.headers = headers;
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Single-flight token refresh: concurrent 401s across many parallel requests
// share one in-flight refresh call instead of each firing their own against
// AUTH.REFRESH (a thundering-herd risk under load).
let refreshPromise: Promise<string> | null = null;

async function performTokenRefresh(): Promise<string> {
  const currentToken = useUserStore.getState().token;

  const response = await axios.create({
    baseURL: import.meta.env.GO_SERVER_API_URL || '/api',
    withCredentials: true
  }).post(AUTH.REFRESH, {}, {
    headers: {
      Authorization: currentToken ? `Bearer ${currentToken}` : ''
    }
  });

  const { token } = response.data;
  if (!token) {
    throw new Error('No token received in refresh response');
  }

  useUserStore.getState().setToken(token);
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  return token;
}

function refreshTokenOnce(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performTokenRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => {

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;


    // If the error is 401 and we haven't already tried to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const token = await refreshTokenOnce();

        // Update the Authorization header and retry the original request
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        // If refresh fails, clear the invalid token and log the user out
        useUserStore.getState().logout();
        showError('Your session has expired. Please log in again.');
        // Without a redirect every page keeps rendering empty data from 401s.
        const target = signInRedirectTarget(window.location.pathname, window.location.search);
        if (target) window.location.assign(target);
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Export the configured instance
export default api;
